import React, { useState } from 'react'
import { InfoCircleFilled } from '@ant-design/icons'
import { Button, Card, Descriptions, Flex, Typography } from 'antd'
import type { DescriptionsProps } from 'antd'
import { useTranslation } from 'react-i18next'
import { SeedPedigree } from 'modules/domain/seedTracker/types'
import { DestinationType, SeedOrderStatus } from 'modules/domain/seedOrders/types'
import type { SeedOrder } from '../SeedOrdersList/SeedOrdersList'
import { getAfterSaleColor } from '../inventoryMt'
import {
  FullWidthCard,
  FullWidthFlex,
  LeftColumn,
  PageBody,
  PedigreeLabel,
  RightColumn,
  StatusLabel,
  TopPadding,
  inventoryInfoCalcDividerStyle,
  inventoryInfoHintIconStyle,
  inventoryInfoHintRowStyle,
  inventoryInfoHintTextStyle,
  inventoryInfoMainCardStyle,
  inventoryInfoRowLabelStyle,
  inventoryInfoRowStyle,
  inventoryInfoRowStackedStyle,
  inventoryInfoRowValueStackedStyle,
  inventoryInfoRowValueStyle,
} from '../styles'
import SeedOrderNotes from './SeedOrderNotes'

function getPedigreeBackgroundColor(pedigree: SeedPedigree): string {
  switch (pedigree) {
    case SeedPedigree.Certified:
      return '#20B02E'
    case SeedPedigree.Registered:
      return '#5C3700'
    case SeedPedigree.Foundation:
      return '#FFAB2E'
    case SeedPedigree.Select:
      return '#037BFF'
    case SeedPedigree.Breeder:
      return '#776D6D'
    default:
      return '#776D6D'
  }
}

function getStatusBackgroundColor(status: SeedOrderStatus): string {
  switch (status) {
    case SeedOrderStatus.Pending:
      return '#037BFF'
    case SeedOrderStatus.Approved:
      return '#2E7D32'
    case SeedOrderStatus.InTransit:
      return '#F57C00'
    case SeedOrderStatus.Completed:
      return '#20B02E'
    case SeedOrderStatus.Cancelled:
      return '#776D6D'
    default:
      return '#776D6D'
  }
}

type Props = {
  order: SeedOrder | null
  onViewInventory?: (companyId: string) => void
}

function formatMt(value?: string): string {
  if (!value?.trim()) {
    return '—'
  }

  const parsed = Number(value.replace(/,/g, ''))
  if (!Number.isFinite(parsed)) {
    return '—'
  }

  return `${parsed.toFixed(2)} MT`
}

const SeedOrderDetailTab: React.FC<Props> = ({ order, onViewInventory }) => {
  const { t } = useTranslation()
  const [isAddingNote, setIsAddingNote] = useState(false)

  const openAddNote = () => {
    setIsAddingNote(true)
  }

  const closeAddNote = () => {
    setIsAddingNote(false)
  }

  const detailsRows: { label: string; value: string }[] = order
    ? [
        { label: t('seedTracker:detail.detailsFields.id'), value: order.id },
        { label: t('seedTracker:detail.detailsFields.orderNumber'), value: order.orderNumber },
        { label: t('seedTracker:detail.detailsFields.purchaseOrderNumber'), value: order.purchaseOrderNumber },
        { label: t('seedTracker:detail.detailsFields.sivNumber'), value: order.sivNumber },
        { label: t('seedTracker:detail.detailsFields.createdDate'), value: order.createdDate },
        { label: t('seedTracker:detail.detailsFields.lastUpdatedDate'), value: order.lastUpdatedDate },
      ]
    : []

  const originRow1: DescriptionsProps['items'] = order
    ? [
        { key: 'grower', label: t('seedTracker:detail.growerFields.grower'), children: order.originGrower },
        // { key: 'location', label: t('seedTracker:detail.growerFields.location'), children: order.originLocation },
        { key: 'region', label: t('seedTracker:detail.growerFields.region'), children: order.originRegion },
      ]
    : []
  const originRow2: DescriptionsProps['items'] = order
    ? [
        {
          key: 'contactName',
          label: t('seedTracker:detail.growerFields.contactName'),
          children: order.originContactName,
        },
        {
          key: 'contactNumber',
          label: t('seedTracker:detail.growerFields.contactNumber'),
          children: order.originContactNumber,
        },
        {
          key: 'contactEmail',
          label: t('seedTracker:detail.growerFields.contactEmail'),
          children: order.originContactEmail,
        },
      ]
    : []

  const destinationRow1: DescriptionsProps['items'] = order
    ? [
        { key: 'grower', label: t('seedTracker:detail.growerFields.grower'), children: order.destinationGrower },
        // { key: 'location', label: t('seedTracker:detail.growerFields.location'), children: order.destinationLocation },
        { key: 'region', label: t('seedTracker:detail.growerFields.region'), children: order.destinationRegion },
      ]
    : []
  const destinationRow2: DescriptionsProps['items'] = order
    ? [
        {
          key: 'contactName',
          label: t('seedTracker:detail.growerFields.contactName'),
          children: order.destinationContactName,
        },
        {
          key: 'contactNumber',
          label: t('seedTracker:detail.growerFields.contactNumber'),
          children: order.destinationContactNumber,
        },
        {
          key: 'contactEmail',
          label: t('seedTracker:detail.growerFields.contactEmail'),
          children: order.destinationContactEmail,
        },
      ]
    : []

  const destinationFarmerRow: DescriptionsProps['items'] = order
    ? [
        {
          key: 'farmerName',
          label: t('seedTracker:detail.growerFields.farmerName'),
          children: order.destinationFarmerName,
        },
        {
          key: 'contactNumber',
          label: t('seedTracker:detail.growerFields.contactNumber'),
          children: order.destinationFarmerContactNumber,
        },
        {
          key: 'contactEmail',
          label: t('seedTracker:detail.growerFields.contactEmail'),
          children: order.destinationFarmerContactEmail,
        },
      ]
    : []

  const destinationOtherRow: DescriptionsProps['items'] = order
    ? [
        {
          key: 'details',
          label: t('seedTracker:detail.growerFields.details'),
          children: order.destinationOtherDetails,
        },
      ]
    : []

  const destinationType = order?.destinationType ?? DestinationType.Grower

  const sourceInventoryButton = order?.originCompanyId ? (
    <Button type="default" onClick={() => onViewInventory?.(order.originCompanyId)} disabled={!onViewInventory}>
      {t('seedTracker:detail.viewInventory', 'View Inventory')}
    </Button>
  ) : null

  const destinationInventoryButton = order?.destinationCompanyId ? (
    <Button type="default" onClick={() => onViewInventory?.(order.destinationCompanyId)} disabled={!onViewInventory}>
      {t('seedTracker:detail.viewInventory', 'View Inventory')}
    </Button>
  ) : null

  return (
    <>
      <PageBody>
        <FullWidthFlex gap="middle">
          <LeftColumn vertical gap="middle">
            <FullWidthCard
              title={
                <FullWidthFlex align="center" justify="space-between">
                  <span>{t('seedTracker:detail.panels.details')}</span>
                  {order && (
                    <StatusLabel backgroundColor={getStatusBackgroundColor(order.status)}>{order.status}</StatusLabel>
                  )}
                </FullWidthFlex>
              }
              variant="borderless"
            >
              <FullWidthFlex vertical gap="small">
                {detailsRows.map((row) => (
                  <FullWidthFlex key={row.label} justify="space-between" align="center">
                    <span>{row.label}</span>
                    <span>{row.value}</span>
                  </FullWidthFlex>
                ))}
              </FullWidthFlex>
            </FullWidthCard>
            <FullWidthCard title={t('seedTracker:detail.panels.pedigreedSeed')} variant="borderless">
              {order && (
                <Flex vertical gap={12}>
                  <FullWidthFlex justify="space-between" align="center">
                    <Flex align="center" gap="small">
                      <PedigreeLabel backgroundColor={getPedigreeBackgroundColor(order.pedigree)}>
                        {order.pedigree}
                      </PedigreeLabel>
                      <span>{order.variety}</span>
                    </Flex>
                    <span>{order.quantityMt} MT</span>
                  </FullWidthFlex>

                  <Card bordered style={inventoryInfoMainCardStyle}>
                    <div style={inventoryInfoHintRowStyle}>
                      <InfoCircleFilled style={inventoryInfoHintIconStyle} />
                      <Typography.Text style={inventoryInfoHintTextStyle}>
                        {t('seedTracker:detail.inventorySnapshot.infoText')}
                      </Typography.Text>
                    </div>

                    <Flex vertical gap={8}>
                      <Flex vertical style={inventoryInfoRowStackedStyle}>
                        <Typography.Text style={inventoryInfoRowLabelStyle}>
                          {t('seedTracker:detail.inventorySnapshot.labels.sourceInventory')}
                        </Typography.Text>
                        <Typography.Text style={inventoryInfoRowValueStackedStyle}>
                          {order.originGrower}
                        </Typography.Text>
                      </Flex>

                      <Flex justify="space-between" align="center" style={inventoryInfoRowStyle}>
                        <Typography.Text style={inventoryInfoRowLabelStyle}>
                          {t('seedTracker:detail.inventorySnapshot.labels.beforeSale')}
                        </Typography.Text>
                        <Typography.Text style={inventoryInfoRowValueStyle}>
                          {formatMt(order.origin_current_mt)}
                        </Typography.Text>
                      </Flex>

                      <Flex justify="space-between" align="center" style={inventoryInfoRowStyle}>
                        <Typography.Text style={inventoryInfoRowLabelStyle}>
                          {t('seedTracker:detail.inventorySnapshot.labels.requested')}
                        </Typography.Text>
                        <Typography.Text style={inventoryInfoRowValueStyle}>
                          {formatMt(order.origin_requested_mt)}
                        </Typography.Text>
                      </Flex>

                      <div style={inventoryInfoCalcDividerStyle} aria-hidden />

                      <Flex justify="space-between" align="center" style={inventoryInfoRowStyle}>
                        <Typography.Text style={inventoryInfoRowLabelStyle}>
                          {t('seedTracker:detail.inventorySnapshot.labels.afterSale')}
                        </Typography.Text>
                        <Typography.Text
                          style={{
                            ...inventoryInfoRowValueStyle,
                            color: getAfterSaleColor(order.origin_after_sale_mt),
                          }}
                        >
                          {formatMt(order.origin_after_sale_mt)}
                        </Typography.Text>
                      </Flex>
                    </Flex>
                  </Card>
                </Flex>
              )}
            </FullWidthCard>
          </LeftColumn>
          <RightColumn vertical gap="middle">
            <FullWidthCard
              title={t('seedTracker:detail.panels.origin')}
              extra={sourceInventoryButton}
              variant="borderless"
            >
              <Flex vertical gap="small">
                <Descriptions column={3} size="small" layout="vertical" colon={false} items={originRow1} />
                <TopPadding>
                  <Descriptions column={3} size="small" layout="vertical" colon={false} items={originRow2} />
                </TopPadding>
              </Flex>
            </FullWidthCard>
            {destinationType === DestinationType.Grower && (
              <FullWidthCard
                title={t('seedTracker:detail.panels.destinationGrower')}
                extra={destinationInventoryButton}
                variant="borderless"
              >
                <Flex vertical gap="small">
                  <Descriptions column={3} size="small" layout="vertical" colon={false} items={destinationRow1} />
                  <TopPadding>
                    <Descriptions column={3} size="small" layout="vertical" colon={false} items={destinationRow2} />
                  </TopPadding>
                </Flex>
              </FullWidthCard>
            )}
            {destinationType === DestinationType.Farmer && (
              <FullWidthCard title={t('seedTracker:detail.panels.destinationFarmer')} variant="borderless">
                <Descriptions column={3} size="small" layout="vertical" colon={false} items={destinationFarmerRow} />
              </FullWidthCard>
            )}
            {destinationType === DestinationType.Other && (
              <FullWidthCard title={t('seedTracker:detail.panels.destinationOther')} variant="borderless">
                <Descriptions column={3} size="small" layout="vertical" colon={false} items={destinationOtherRow} />
              </FullWidthCard>
            )}
            <FullWidthCard
              title={t('seedTracker:drawerPanels.inventoryNotes', 'Notes')}
              extra={!isAddingNote ? <Button onClick={openAddNote}>{t('notes:form.add', 'Add note')}</Button> : null}
              variant="borderless"
            >
              <SeedOrderNotes
                order={order}
                isAddingNote={isAddingNote}
                onCancelAddNote={closeAddNote}
                onSaveAddNote={closeAddNote}
              />
            </FullWidthCard>
          </RightColumn>
        </FullWidthFlex>
      </PageBody>
    </>
  )
}

export default SeedOrderDetailTab
